import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Braces, Check, ChevronDown, Loader2, Mail, Trash2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import {
  fetchCtoMembers,
  fetchMemberActivity,
  formatDate,
  formatToolName,
  formatWhen,
  groupActivityByConnection,
  type CtoMemberRow,
  type McpActivityRow,
} from '../../lib/ctoMembers';
import {
  defaultUsageNote,
  fetchMemberUsageQuota,
  saveMemberUsageLimits,
  sendUsageNote,
  type MemberUsageQuota,
  type UsageNoteKind,
} from '../../lib/ctoUsageLimits';
import {
  EFFORT_RANK,
  CATEGORY_LABEL,
  FINDING_CATEGORIES,
  FINDING_STATUSES,
  effortIsLow,
  impactIsHigh,
  deleteReview,
  fetchReviewsForSubscriber,
  flattenFindings,
  subscribeCtoReviewsLive,
  summarizeRepos,
  updateFindingStatus,
  updateReview,
  type CtoReviewRow,
  type FindingStatus,
  type FindingWithContext,
} from '../../lib/ctoReviews';
import { fetchKnownsForSubscriber, type CtoRepoKnownRow } from '../../lib/ctoKnowns';
import {
  CtoEffortToggle,
  CtoFindingBoard,
  CtoFindingViewToggle,
  CtoImpactToggle,
  type FindingView,
} from './CtoFindingBoard';
import { CtoFindingMatrix } from './CtoFindingMatrix';
import { CtoFindingRow } from './CtoFindingRow';
import { CtoRepoList } from './CtoRepoList';
import { RepoChip, RepoMark } from './RepoMark';
import { ClientIcon, ConnectedClientBadges } from './ClientIcon';
import { resolveCallSource, sourceHintFromCall } from '../../lib/mcpClientIcons';
import SubscriberJsonModal from '../Subscribers/SubscriberJsonModal';

/**
 * One member: what their assistant has been doing on the connector, and what came out of it.
 *
 * Activity on the left, observations on the right. The two halves answer different
 * questions — "is this person using it" and "what did we find" — and the second is worthless
 * without the first, because a member with no calls has no findings for a reason.
 */

type MemberTab = 'activity' | 'reviews' | 'codebases';
type ImpactFilter = 'all' | 'high' | 'low';
type EffortFilter = 'all' | 'low' | 'high';

const TABS: { id: MemberTab; label: string }[] = [
  { id: 'activity', label: 'Activity' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'codebases', label: 'Codebases' },
];

function parseMemberTab(raw: string | null): MemberTab {
  if (raw === 'reviews' || raw === 'codebases') return raw;
  if (raw === 'repos') return 'codebases';
  return 'activity';
}

const OUTCOME_STYLE: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  error: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  denied: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

function formatShortDate(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const date = new Date(then);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

function formatAgo(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function ActivityRow({
  call,
  selected,
  onSelect,
}: {
  call: McpActivityRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const source = resolveCallSource(call.clientName, call.requestArgs);
  const iconName = sourceHintFromCall(call.requestArgs) || call.clientName;
  return (
    <tr
      onClick={onSelect}
      className={`cursor-pointer ${
        selected
          ? 'bg-fuchsia-50 dark:bg-fuchsia-950/30'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
      }`}
    >
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <ClientIcon clientName={iconName} size="sm" />
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {source}
          </span>
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-gray-800 dark:text-gray-100">
            {formatShortDate(call.occurredAt)}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatAgo(call.occurredAt)}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-100">
        {formatToolName(call.toolName)}
      </td>
    </tr>
  );
}

function ActivityDetail({ call }: { call: McpActivityRow | null }) {
  const [jsonOpen, setJsonOpen] = useState(false);

  if (!call) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 p-8 text-center">
        Select a call to see the ask and what came back.
      </p>
    );
  }

  const source = resolveCallSource(call.clientName, call.requestArgs);
  const iconName = sourceHintFromCall(call.requestArgs) || call.clientName;
  const asked = call.requestArgs
    ? JSON.stringify(call.requestArgs, null, 2)
    : null;

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClientIcon clientName={iconName} />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatToolName(call.toolName)}
            </h3>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {source} · {formatShortDate(call.occurredAt)} · {formatAgo(call.occurredAt)}
            {call.durationMs !== null ? ` · ${call.durationMs.toLocaleString()}ms` : ''}
            {call.connectionId
              ? ` · connection ${call.connectionId.slice(0, 8)}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          <Chip label={call.outcome} className={OUTCOME_STYLE[call.outcome] ?? undefined} />
          {call.errorCode ? <Chip label={call.errorCode} /> : null}
          <button
            type="button"
            onClick={() => setJsonOpen(true)}
            title="What we stored for this call"
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <Braces className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <SubscriberJsonModal
        isOpen={jsonOpen}
        onClose={() => setJsonOpen(false)}
        jsonString={JSON.stringify(call, null, 2)}
      />

      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Ask</div>
        {asked ? (
          <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5 overflow-x-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
            {asked}
          </pre>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            We did not store what was sent to this tool.
          </p>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Response</div>
        {call.responseSummary ? (
          <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5 overflow-x-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
            {call.responseSummary}
          </pre>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No response was stored for this call.
          </p>
        )}
      </div>

    </div>
  );
}

function statusFilterLabel(status: string): string {
  if (status === 'shipped') return 'Closed';
  if (status === 'fixed-since') return 'Fixed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function FilterSelect({
  value,
  onChange,
  ariaLabel,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const selected = options.find((option) => option.value === value)?.label ?? value;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span className="relative inline-flex" ref={rootRef}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`inline-flex items-center gap-2 pl-3.5 pr-2.5 py-2 rounded-xl border text-sm font-medium shadow-sm ${
          open
            ? 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-200'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <span>{selected}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-40 mt-1.5 min-w-full w-max rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 py-1 shadow-lg"
        >
          {options.map((option) => {
            const active = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3.5 py-2 text-sm whitespace-nowrap ${
                    active
                      ? 'bg-fuchsia-50 text-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-200'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </span>
  );
}

function Chip({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded whitespace-nowrap ${
        className ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      }`}
    >
      {label}
    </span>
  );
}

function EmptyFindings({ recorded }: { recorded: boolean }) {
  return (
    <p className="text-sm text-gray-500 dark:text-gray-400 p-10 text-center">
      {recorded
        ? 'No observations match those filters.'
        : 'Nothing recorded yet. Findings land here as sessions return them.'}
    </p>
  );
}

function sortFindings(rows: FindingWithContext[]): FindingWithContext[] {
  return [...rows].sort((a, b) => {
    const impact = (b.impact ?? 0) - (a.impact ?? 0);
    if (impact !== 0) return impact;
    return (a.effort ? EFFORT_RANK[a.effort] : 99) - (b.effort ? EFFORT_RANK[b.effort] : 99);
  });
}

export default function CtoMemberDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseMemberTab(searchParams.get('tab'));
  const setActiveTab = useCallback(
    (tab: MemberTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'activity') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [member, setMember] = useState<CtoMemberRow | null>(null);
  const [activity, setActivity] = useState<McpActivityRow[]>([]);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [reviews, setReviews] = useState<CtoReviewRow[]>([]);
  const [knowns, setKnowns] = useState<CtoRepoKnownRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const reposHydrated = useRef(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [impactFilter, setImpactFilter] = useState<ImpactFilter>('all');
  const [effortFilter, setEffortFilter] = useState<EffortFilter>('all');
  const [view, setView] = useState<FindingView>('table');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      // The member list is a four-table stitch; reusing it keeps this page from
      // disagreeing with the one that decides who has access.
      const [members, calls, rows, stored] = await Promise.all([
        fetchCtoMembers(),
        fetchMemberActivity(id),
        fetchReviewsForSubscriber(id),
        fetchKnownsForSubscriber(id),
      ]);
      setMember(members.find((m) => m.subscriberId === id) ?? null);
      setActivity(calls);
      setSelectedCallId((prev) =>
        prev && calls.some((c) => c.id === prev) ? prev : (calls[0]?.id ?? null),
      );
      setReviews(rows);
      setKnowns(stored);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load the member');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshActivity = useCallback(async () => {
    if (!id) return;
    try {
      const calls = await fetchMemberActivity(id);
      setActivity(calls);
      setSelectedCallId((prev) =>
        prev && calls.some((c) => c.id === prev) ? prev : (calls[0]?.id ?? null),
      );
    } catch {
      /* Live refresh — do not toast on every tick. */
    }
  }, [id]);

  const refreshReviews = useCallback(async () => {
    if (!id) return;
    try {
      const [rows, stored] = await Promise.all([
        fetchReviewsForSubscriber(id),
        fetchKnownsForSubscriber(id),
      ]);
      setReviews(rows);
      setKnowns(stored);
    } catch {
      /* Live refresh — do not toast on every tick. */
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    return subscribeCtoReviewsLive(() => void refreshReviews(), { subscriberId: id });
  }, [id, refreshReviews]);

  useEffect(() => {
    if (!id || activeTab !== 'activity') return;

    const tick = () => {
      if (document.hidden) return;
      void refreshActivity();
    };

    const channel = supabase
      .channel(`mcp_usage_events:member:${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mcp_usage_events',
          filter: `subscriber_id=eq.${id}`,
        },
        () => {
          void refreshActivity();
        },
      )
      .subscribe();

    const interval = window.setInterval(tick, 8_000);
    document.addEventListener('visibilitychange', tick);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
      void supabase.removeChannel(channel);
    };
  }, [id, activeTab, refreshActivity]);

  const activityGroups = useMemo(() => groupActivityByConnection(activity), [activity]);

  const repos = useMemo(() => summarizeRepos(reviews, knowns), [reviews, knowns]);
  const allFindings = useMemo(() => flattenFindings(reviews), [reviews]);

  useEffect(() => {
    const keys = repos.map((row) => row.repo);
    if (keys.length === 0) {
      setSelectedRepos([]);
      return;
    }
    setSelectedRepos((prev) => {
      const known = new Set(keys);
      const kept = prev.filter((repo) => known.has(repo));
      if (keys.length === 1) return keys;
      if (!reposHydrated.current) return keys;
      return kept;
    });
    reposHydrated.current = true;
  }, [repos]);

  const findings = useMemo(() => {
    const selected = new Set(selectedRepos);
    const filtered = allFindings.filter((f) => {
      if (!selected.has(f.repo)) return false;
      if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
      if (statusFilter !== 'all' && f.status !== statusFilter) return false;
      if (impactFilter === 'high' && impactIsHigh(f.impact) !== true) return false;
      if (impactFilter === 'low' && impactIsHigh(f.impact) !== false) return false;
      if (effortFilter === 'low' && effortIsLow(f.effort) !== true) return false;
      if (effortFilter === 'high' && effortIsLow(f.effort) !== false) return false;
      return true;
    });
    return sortFindings(filtered);
  }, [allFindings, selectedRepos, categoryFilter, statusFilter, impactFilter, effortFilter]);

  const toggleRepo = (repo: string) => {
    setSelectedRepos((prev) =>
      prev.includes(repo) ? prev.filter((item) => item !== repo) : [...prev, repo],
    );
  };

  const setStatus = async (findingId: string, status: FindingStatus) => {
    try {
      await updateFindingStatus(findingId, status);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not update the finding');
    }
  };

  const toggleComplete = async (review: CtoReviewRow) => {
    try {
      await updateReview(review.id, {
        status: review.status === 'complete' ? 'running' : 'complete',
      });
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not update the review');
    }
  };

  const removeReview = async (review: CtoReviewRow) => {
    if (!window.confirm(`Delete the review of ${review.repo} and its findings?`)) return;
    try {
      await deleteReview(review.id);
      await load();
      toast.success('Review deleted');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not delete the review');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-24">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!member) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          No activation for that member.
        </p>
        <button
          type="button"
          onClick={() => navigate('/cto/members')}
          className="text-sm text-fuchsia-600 dark:text-fuchsia-400 hover:underline"
        >
          Back to members
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/cto/members')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Members
      </button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {member.name ?? member.email}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {member.email} · {member.tierName}
            {member.paid ? '' : ' (no paid tools)'} · activated {formatDate(member.activatedAt)} ·{' '}
            {member.totalCalls.toLocaleString()} calls · last {formatWhen(member.lastToolAt)}
          </p>
          <div className="mt-3">
            <ConnectedClientBadges clients={member.clients} />
          </div>
        </div>
        <UsageLimitsPanel
          subscriberId={member.subscriberId}
          firstName={(member.name ?? '').split(/\s+/)[0] || null}
          activity={activity}
          onSaved={() => void load()}
        />
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-fuchsia-600 text-gray-900 dark:text-white'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
            {tab.id === 'reviews' && allFindings.length > 0 ? (
              <span className="ml-1.5 font-normal text-gray-400">{allFindings.length}</span>
            ) : null}
            {tab.id === 'codebases' && repos.length > 0 ? (
              <span className="ml-1.5 font-normal text-gray-400">{repos.length}</span>
            ) : null}
          </button>
        ))}
      </div>

      {activeTab === 'activity' ? (
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,6fr)_minmax(0,4fr)] gap-6 items-start">
        <aside className="min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Activity
              <span className="ml-2 font-normal text-gray-400">{activity.length}</span>
            </h2>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {activity.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 p-6 text-center">
                No calls recorded. Either they have not connected an assistant yet, or they
                connected and never asked it anything.
              </p>
            ) : (
              <div className="max-h-[36rem] overflow-auto">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                    <tr className="text-[11px] uppercase tracking-wide text-gray-400">
                      <th className="px-3 py-2 font-medium">Source</th>
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">Tool</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {activityGroups.map((group) => {
                      const lead = group.calls[0];
                      const source = lead
                        ? resolveCallSource(lead.clientName, lead.requestArgs)
                        : null;
                      return (
                      <Fragment key={group.connectionId ?? 'ungrouped'}>
                        <tr className="bg-gray-50 dark:bg-gray-900/50">
                          <td
                            colSpan={3}
                            className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                          >
                            {group.connectionId
                              ? `${source ?? 'Connection'} · ${group.calls.length}`
                              : `Ungrouped · ${group.calls.length}`}
                          </td>
                        </tr>
                        {group.calls.map((call) => (
                          <ActivityRow
                            key={call.id}
                            call={call}
                            selected={call.id === selectedCallId}
                            onSelect={() => setSelectedCallId(call.id)}
                          />
                        ))}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </aside>
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 min-h-[16rem]">
          <ActivityDetail
            call={activity.find((c) => c.id === selectedCallId) ?? null}
          />
        </section>
      </div>
      ) : activeTab === 'reviews' ? (
        <section>
          {repos.length > 0 && (
            <div className="flex flex-wrap items-center gap-2.5 mb-4">
              {repos.map((r) => (
                <RepoChip
                  key={r.repo}
                  repo={r.repo}
                  facts={[
                    `${r.openFindings} ${r.openFindings === 1 ? 'open ticket' : 'open tickets'}`,
                  ]}
                >
                  <button
                    type="button"
                    onClick={() => toggleRepo(r.repo)}
                    aria-pressed={selectedRepos.includes(r.repo)}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-sm ${
                      selectedRepos.includes(r.repo)
                        ? 'border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-900/20 text-fuchsia-700 dark:text-fuchsia-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                        selectedRepos.includes(r.repo)
                          ? 'border-fuchsia-600 bg-fuchsia-600 text-white'
                          : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-900'
                      }`}
                      aria-hidden
                    >
                      {selectedRepos.includes(r.repo) ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <RepoMark repo={r.repo} />
                  </button>
                </RepoChip>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5 mb-4">
            <FilterSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              ariaLabel="Filter by area"
              options={[
                { value: 'all', label: 'All areas' },
                ...FINDING_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
              ]}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              ariaLabel="Filter by status"
              options={[
                { value: 'all', label: 'Any status' },
                ...FINDING_STATUSES.map((s) => ({ value: s, label: statusFilterLabel(s) })),
              ]}
            />
            <CtoImpactToggle value={impactFilter} onChange={setImpactFilter} />
            <CtoEffortToggle value={effortFilter} onChange={setEffortFilter} />
            <CtoFindingViewToggle view={view} onChange={setView} />
          </div>

          {view === 'narrative' ? (
            allFindings.length === 0 ? (
              <EmptyFindings recorded={false} />
            ) : findings.length === 0 ? (
              <EmptyFindings recorded />
            ) : (
              <CtoFindingBoard
                findings={findings}
                onStatus={(id, next) => void setStatus(id, next)}
              />
            )
          ) : view === 'graph' ? (
            allFindings.length === 0 ? (
              <EmptyFindings recorded={false} />
            ) : (
              <CtoFindingMatrix findings={findings} />
            )
          ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {allFindings.length === 0 ? (
              <EmptyFindings recorded={false} />
            ) : findings.length === 0 ? (
              <EmptyFindings recorded />
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {findings.map((f) => (
                  <CtoFindingRow
                    key={f.id}
                    finding={f}
                    onStatus={(status) => void setStatus(f.id, status)}
                  />
                ))}
              </ul>
            )}
          </div>
          )}

          {reviews.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                Runs
              </h3>
              <ul className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {reviews.map((r) => (
                  <li key={r.id} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 dark:text-white truncate">
                        <RepoMark repo={r.repo} iconClassName="h-3.5 w-3.5" />
                        {r.repoRef ? (
                          <span className="font-mono text-gray-400"> @{r.repoRef}</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(r.reviewedAt)} · {r.source} · {r.status} ·{' '}
                        {r.findings.length} findings
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void toggleComplete(r)}
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
                    >
                      {r.status === 'complete' ? 'Reopen' : 'Mark complete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void removeReview(r)}
                      className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                      title="Delete review"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ) : (
        <section>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-3xl">
            Codebases from this member&apos;s reviews and knowns. Linking is a stub
            until we can open the repository from here.
          </p>
          <CtoRepoList
            reviews={reviews}
            knowns={knowns}
            showPeople={false}
            onOpenReviews={(repo) => {
              setSelectedRepos([repo]);
              setActiveTab('reviews');
            }}
          />
        </section>
      )}
    </div>
  );
}

function usageStatusLabel(quota: MemberUsageQuota): string {
  if (quota.unlimited || quota.limit == null) return 'Unlimited';
  if (quota.exhausted) return 'Exhausted';
  if (quota.warn) return 'Near limit';
  return 'On track';
}

function usagePercent(quota: MemberUsageQuota): number | null {
  if (quota.unlimited || quota.limit == null || quota.limit <= 0) return null;
  return Math.min(100, Math.round((quota.used / quota.limit) * 100));
}

function monthCallBreakdown(activity: McpActivityRow[]): {
  total: number;
  overhead: number;
} {
  const start = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1);
  let total = 0;
  let overhead = 0;
  for (const call of activity) {
    if (Date.parse(call.occurredAt) < start) continue;
    total += 1;
    if (call.toolName === 'cto_whoami') overhead += 1;
  }
  return { total, overhead };
}

function UsageLimitsPanel({
  subscriberId,
  firstName,
  activity,
  onSaved,
}: {
  subscriberId: string;
  firstName: string | null;
  activity: McpActivityRow[];
  onSaved: () => void;
}) {
  const [quota, setQuota] = useState<MemberUsageQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setQuota(await fetchMemberUsageQuota(subscriberId));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not load usage limits');
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resets = quota?.resetsAt
    ? new Date(quota.resetsAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
  const percent = quota ? usagePercent(quota) : null;
  const monthCalls = monthCallBreakdown(activity);
  const status = quota ? usageStatusLabel(quota) : null;
  const statusTone =
    quota?.exhausted
      ? 'text-rose-600 dark:text-rose-400'
      : quota?.warn
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-gray-500 dark:text-gray-400';
  const barTone = quota?.exhausted
    ? 'bg-rose-500'
    : quota?.warn
      ? 'bg-amber-500'
      : 'bg-fuchsia-600';

  return (
    <section className="w-64 shrink-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          Monthly usage limit
        </h2>
        <button
          type="button"
          disabled={loading || !quota}
          onClick={() => setOpen(true)}
          className="text-xs px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Edit
        </button>
      </div>
      {loading || !quota || !status ? (
        <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      ) : (
        <p className={`mt-1 text-xs ${statusTone}`}>
          {status}
          {percent != null ? ` · ${percent}% used` : ''}
          <span className="block mt-0.5 tabular-nums text-gray-600 dark:text-gray-300">
            {quota.unlimited || quota.limit == null
              ? `${quota.used.toLocaleString()} billed this month`
              : `${quota.used.toLocaleString()} / ${quota.limit.toLocaleString()}`}
            {resets ? ` · resets ${resets}` : ''}
          </span>
          {monthCalls.total > 0 ? (
            <span className="block mt-0.5 tabular-nums text-gray-500 dark:text-gray-400">
              {monthCalls.total.toLocaleString()} calls
              {monthCalls.overhead > 0
                ? ` · ${monthCalls.overhead.toLocaleString()} overhead`
                : ''}
            </span>
          ) : null}
        </p>
      )}
      {quota && percent != null ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
          <div className={`h-full ${barTone}`} style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {open && quota ? (
        <UsageLimitsModal
          subscriberId={subscriberId}
          firstName={firstName}
          quota={quota}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            await refresh();
            onSaved();
          }}
        />
      ) : null}
    </section>
  );
}

function UsageLimitsModal({
  subscriberId,
  firstName,
  quota,
  onClose,
  onSaved,
}: {
  subscriberId: string;
  firstName: string | null;
  quota: MemberUsageQuota;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const planDefault = quota.tierMonthlyCallLimit;
  const currentCap =
    quota.monthlyCallLimitOverride ?? quota.tierMonthlyCallLimit;
  const [overrideInput, setOverrideInput] = useState(
    currentCap == null ? '' : String(currentCap),
  );
  const [unlimited, setUnlimited] = useState(quota.usageUnlimited);
  const [noteKind, setNoteKind] = useState<UsageNoteKind>('credit');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const nextCap = overrideInput.trim() === '' ? null : Number(overrideInput.trim());
  const addedCalls =
    nextCap != null && currentCap != null ? Math.max(0, nextCap - currentCap) : 0;

  useEffect(() => {
    const draft = defaultUsageNote(noteKind, {
      creditCalls: addedCalls,
      firstName,
    });
    setSubject(draft.subject);
    setBody(draft.body);
  }, [noteKind, addedCalls, firstName]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const save = async (sendEmail: boolean) => {
    const override = nextCap;
    if (
      !unlimited &&
      override !== null &&
      (!Number.isFinite(override) || override < 0 || !Number.isInteger(override))
    ) {
      toast.error('Limit must be a whole number');
      return;
    }
    if (sendEmail && !body.trim()) {
      toast.error('Write a short note before sending.');
      return;
    }

    const storedOverride =
      unlimited || override == null || override === planDefault ? null : override;

    setSaving(true);
    try {
      await saveMemberUsageLimits(subscriberId, quota.livemode, {
        monthlyCallLimitOverride: storedOverride,
        usageUnlimited: unlimited,
        creditCalls: 0,
      });
      if (sendEmail) {
        await sendUsageNote({
          subscriberId,
          subject,
          personalMessage: body,
          includeUpgradeCta: noteKind === 'exhausted',
        });
        toast.success('Limits saved and note sent');
      } else {
        toast.success('Limits saved');
      }
      await onSaved();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not save usage limits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div
        role="dialog"
        aria-labelledby="usage-limit-title"
        className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 id="usage-limit-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit monthly usage limit
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Starts at their plan default
              {planDefault == null ? ' (unlimited)' : ` (${planDefault.toLocaleString()})`}.
              Raise the number to give them a higher cap.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Monthly limit
            <input
              type="number"
              min={0}
              step={1}
              disabled={unlimited || saving}
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              placeholder={planDefault == null ? 'Unlimited' : String(planDefault)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white disabled:opacity-50"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={unlimited}
              disabled={saving}
              onChange={(e) => setUnlimited(e.target.checked)}
              className="rounded border-gray-300 text-fuchsia-600 focus:ring-fuchsia-500"
            />
            Unlimited
          </label>

          <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-3 space-y-3">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Email</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setNoteKind('credit')}
                className={`text-xs px-2.5 py-1 rounded-lg border ${
                  noteKind === 'credit'
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Credited more calls
              </button>
              <button
                type="button"
                onClick={() => setNoteKind('exhausted')}
                className={`text-xs px-2.5 py-1 rounded-lg border ${
                  noteKind === 'exhausted'
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Out of calls + upgrade
              </button>
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={saving}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
              placeholder="Subject"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={saving}
              rows={5}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save(false)}
            disabled={saving}
            className="px-3 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => void save(true)}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save & send email'}
          </button>
        </div>
      </div>
    </div>
  );
}
